import React from 'react';
import FirmField from './FirmField';
import useFirmPatch from './useFirmPatch';

/**
 * FirmKeyFactsWidget — core firm facts in the candidate-widget design language:
 * a colored headline panel (AUM), then labeled editable fields grouped by
 * section with dividers.
 */
export default function FirmKeyFactsWidget({ firm, firmId, onRefresh }) {
    const { local, patch } = useFirmPatch(firm, firmId, onRefresh);

    return (
        <div className="space-y-4">
            {/* Headline — AUM in a colored panel (mirrors the comp target panel) */}
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <FirmField
                    label="Assets Under Management"
                    value={local.aum}
                    display={local.aum ? `$${local.aum}` : null}
                    placeholder="Add AUM"
                    onSave={(v) => patch({ aum: v })}
                    accent="emerald"
                />
            </div>

            <hr className="border-gray-200" />

            {/* Firm details */}
            <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Firm Details</label>
                <div className="grid grid-cols-2 gap-2">
                    <FirmField label="Employees" type="number" value={local.totalEmployees}
                        display={local.totalEmployees?.toLocaleString()}
                        onSave={(v) => patch({ totalEmployees: v == null ? null : parseInt(v, 10) || 0 })} />
                    <FirmField label="Industry" value={local.industry} onSave={(v) => patch({ industry: v })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <FirmField label="Payout Structure" value={local.payoutStructure} onSave={(v) => patch({ payoutStructure: v })} />
                    <FirmField label="Bonus Date" type="date"
                        value={local.bonusDate ? local.bonusDate.slice(0, 10) : ''}
                        display={local.bonusDate ? new Date(local.bonusDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null}
                        onSave={(v) => patch({ bonusDate: v })} />
                </div>
            </div>

            <hr className="border-gray-200" />

            {/* Terms */}
            <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Terms</label>
                <div className="grid grid-cols-2 gap-2">
                    <FirmField label="PnL Frequency" type="select"
                        options={[{ value: '', label: '—' }, { value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'annually', label: 'Annually' }]}
                        value={local.pnlPayoutFrequency || ''}
                        display={local.pnlPayoutFrequency ? local.pnlPayoutFrequency.charAt(0).toUpperCase() + local.pnlPayoutFrequency.slice(1) : null}
                        onSave={(v) => patch({ pnlPayoutFrequency: v })} />
                    <FirmField label="Put Up Capital" type="select"
                        options={[{ value: '', label: '—' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
                        value={local.putUpCapital === true ? 'true' : local.putUpCapital === false ? 'false' : ''}
                        display={local.putUpCapital === true ? 'Yes' : local.putUpCapital === false ? 'No' : null}
                        onSave={(v) => patch({ putUpCapital: v === 'true' ? true : v === 'false' ? false : null })} />
                </div>
            </div>
        </div>
    );
}
